// Based on Tiptap's Blockquote extension:
// https://github.com/ueberdosis/tiptap/blob/main/packages/extension-blockquote/src/blockquote.ts

import { mergeAttributes, Node, NodeConfig } from '@tiptap/core';
import _ from 'lodash';
import css from './HighlightBlock.module.scss';

export const HIGHLIGHT_BLOCK_NAME = 'highlightBlock';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [HIGHLIGHT_BLOCK_NAME]: {
      /**
       * Toggle a highlight block
       */
      toggleHighlightBlock: () => ReturnType;
    };
  }
}

export enum HighlightBlockAlignment {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum HighlightBlockColor {
  BLUE = 'blue',
  GRAY = 'gray',
  GREEN = 'green',
  PURPLE = 'purple',
  RED = 'red',
  TURQUOISE = 'turquoise',
}

interface IHighlightBlockOptions {
  alignments: HighlightBlockAlignment[];
  colors: HighlightBlockColor[];
  defaultAlignment: HighlightBlockAlignment;
  defaultColor: HighlightBlockColor;
}

export const HighlightBlock = (
  config: Partial<NodeConfig<IHighlightBlockOptions>> = {}
) =>
  Node.create<IHighlightBlockOptions>(
    _.extend(
      {
        /** The name of the block. This is used to represent the block in the editor's JSON output.
         * Changing this will break old content!
         * See: https://tiptap.dev/guide/custom-extensions#name
         */
        name: HIGHLIGHT_BLOCK_NAME,

        /** The content group this node belongs to.
         * See: https://prosemirror.net/docs/ref/#model.NodeSpec.group
         */
        group: 'block',

        /** What content is valid inside the block.
         * See: https://prosemirror.net/docs/guide/#schema.content_expressions
         */
        content: 'paragraph*',

        /** Make the block draggable within the editor.
         * See: https://prosemirror.net/docs/ref/#model.NodeSpec.draggable
         */
        draggable: true,

        /** The element will be preserved when copy/pasting content.
         * See: https://prosemirror.net/docs/ref/#model.NodeSpec.defining
         */
        defining: true,

        addOptions() {
          return {
            alignments: _.values(HighlightBlockAlignment),
            colors: _.values(HighlightBlockColor),
            defaultAlignment: HighlightBlockAlignment.CENTER,
            defaultColor: HighlightBlockColor.BLUE,
          };
        },

        /** HighlightBlock instance attributes. These values are rendered as HTML attributes.
         * See: https://tiptap.dev/guide/custom-extensions#attributes
         */
        addAttributes() {
          return {
            alignment: {
              default: this.options.defaultAlignment,
              // Parse the HTML element and return the alignment attribute if it's valid.
              parseHTML: (element): HighlightBlockAlignment => {
                const alignment = element.getAttribute('data-alignment');
                return _.includes(this.options.alignments, alignment)
                  ? (alignment as HighlightBlockAlignment)
                  : this.options.defaultAlignment;
              },
              // Render the alignment attribute as an HTML attribute.
              renderHTML: (attributes) => ({
                'data-alignment': attributes.alignment,
              }),
            },
            color: {
              default: this.options.defaultColor,
              // Parse the HTML element and return the color attribute if it's valid.
              parseHTML: (element): HighlightBlockColor => {
                const color = element.getAttribute('data-color');
                return _.includes(this.options.colors, color)
                  ? (color as HighlightBlockColor)
                  : this.options.defaultColor;
              },
              // Render the color attribute as an HTML attribute.
              renderHTML: (attributes) => ({ 'data-color': attributes.color }),
            },
          };
        },

        addCommands() {
          return {
            toggleHighlightBlock:
              () =>
              ({ commands }) => {
                return commands.toggleWrap(this.name);
              },
          };
        },

        parseHTML() {
          return [
            {
              tag: 'div',
              attrs: { 'data-type': HIGHLIGHT_BLOCK_NAME },
            },
          ];
        },

        renderHTML({ HTMLAttributes }) {
          return [
            'div',
            mergeAttributes(HTMLAttributes, {
              class: css.highlightBlock,
              'data-type': HIGHLIGHT_BLOCK_NAME,
            }),
            0,
          ];
        },
      },
      config
    )
  );
